import { h, render } from 'preact';

import { createApiClient } from './api';
import { createCollector } from './context/collector';
import { detectSentry, resolveMetadata } from './context/host';
import { loadFonts } from './fonts';
import { setLocale } from './i18n';
import { App } from './ui/App';
import styles from './ui/styles.css?inline';

import type { FeedbackOptions } from './types';

export type * from './types';

export interface FeedbackInstance {
  open(): void;
  destroy(): void;
}

let instance: FeedbackInstance | null = null;

export function init(options: FeedbackOptions): FeedbackInstance {
  if (instance) return instance;
  if (!options.endpoint || !options.project) {
    throw new Error('HoppingerFeedback.init needs `endpoint` and `project`');
  }

  const endpoint = options.endpoint.replace(/\/+$/, '');
  if (options.fonts !== false) loadFonts(endpoint);
  setLocale(options.locale);
  const host = document.createElement('hoppinger-feedback');
  host.setAttribute('style', 'position:fixed;top:0;left:0;width:0;height:0;z-index:2147483000;');

  const collector = createCollector({
    ignoreUrl: (url) => url.startsWith(endpoint),
    ignoreNode: (node) => node === host,
    extra: () => {
      const metadata = resolveMetadata(options.metadata);
      const sentry = detectSentry(options.sentryEventId);
      return { ...(metadata ? { metadata } : {}), ...(sentry ? { sentry } : {}) };
    },
  });
  collector.install();

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = styles;
  const mount = document.createElement('div');
  shadow.append(style, mount);
  // init() may run from <head>; the collector is already recording, the UI waits for <body>.
  if (document.body) document.body.append(host);
  else
    document.addEventListener('DOMContentLoaded', () => document.body.append(host), { once: true });

  let openHandler: () => void = () => {};

  render(
    h(App, {
      api: createApiClient(endpoint, options.project),
      collector,
      host,
      launcher: options.launcher ?? true,
      side: options.side ?? 'right',
      project: options.project,
      pins: options.pins ?? true,
      privacyUrl: options.privacyUrl,
      registerOpen: (fn: () => void) => {
        openHandler = fn;
      },
    }),
    mount,
  );

  instance = {
    open: () => openHandler(),
    destroy() {
      render(null, mount);
      host.remove();
      collector.uninstall();
      instance = null;
    },
  };
  return instance;
}

export function open(): void {
  instance?.open();
}

export function destroy(): void {
  instance?.destroy();
}
